/**
 * RiskTimeline — TASK-688.
 *
 * Not a chart. The answer to *"why did you hold this position for this client in Q2?"* —
 * documented appetite over time against the mandate band, every point quoting the sentence
 * that moved it.
 *
 * Three rules, all of them honesty rules:
 *
 * 1. **Step, never interpolate.** The line holds flat between observations and jumps at one.
 *    A smoothed curve would assert an appetite on a date the client never spoke.
 * 2. **Two distinct DATES or nothing.** Several observations recorded on one day are one day's
 *    intake; drawing a "history" across zero elapsed time is the misleading picture this
 *    widget most invites. The backend decides; this renders what it says.
 * 3. **Documented history, not a compliance attestation.** Said in the widget's own footer,
 *    because a timeline like this will otherwise end up in a FinSA pack unreviewed.
 *
 * Hand-rolled SVG rather than a chart library: a step line + a band + a date axis + a scrubber
 * is less code here than the configuration needed to stop a charting library smoothing,
 * and it cannot silently interpolate. No autoplay, so there is no timer to leak on unmount.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Quote } from 'lucide-react';

import { httpClient } from '../../../lib/httpClient';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetErrorState } from '../../ui/WidgetErrorState';

interface TimelinePoint {
  at: string;
  kind: string;
  level: number | null;
  appetite: string | null;
  what?: string | null;
  tag?: string | null;
  direction: number;
  excerpt?: string | null;
  locator?: { transcript_line?: number; page?: number; line?: number } | null;
  observation_count: number;
  superseded: boolean;
}

interface TimelineResponse {
  points: TimelinePoint[];
  plotted_count: number;
  distinct_dates: number;
  transition_count: number;
  band: { profile: string; min_level: number; max_level: number } | null;
  sufficient: boolean;
  uncited_points: number;
  reason: string | null;
}

const APPETITE_ROWS = [
  { level: 3, label: 'Aggressive' },
  { level: 2, label: 'Balanced' },
  { level: 1, label: 'Conservative' },
];

// Plot geometry. viewBox + preserveAspectRatio keeps it responsive without measuring the DOM.
const W = 640;
const H = 200;
const PAD = { top: 12, right: 12, bottom: 26, left: 88 };

function levelY(level: number): number {
  // 3 rows, level 3 at the top.
  const usable = H - PAD.top - PAD.bottom;
  return PAD.top + ((3 - level) / 2) * usable;
}

function locatorLabel(locator?: TimelinePoint['locator']): string | null {
  if (!locator) return null;
  if (locator.transcript_line != null) return `transcript line ${locator.transcript_line}`;
  if (locator.page != null) return `page ${locator.page}`;
  return null;
}

export interface RiskTimelineProps {
  clientId: string;
  title?: string;
}

export function RiskTimeline({ clientId, title }: RiskTimelineProps) {
  const query = useQuery({
    queryKey: ['risk-timeline', clientId],
    queryFn: () =>
      httpClient.get<TimelineResponse>(`/api/knowledge-graph/${clientId}/risk-timeline`),
    enabled: Boolean(clientId),
  });

  const data = query.data;
  const plotted = useMemo(
    () => (data?.points ?? []).filter((p): p is TimelinePoint & { level: number } => p.level !== null),
    [data?.points],
  );

  // The scrubber selects an OBSERVATION, not a continuous time value: only dates where
  // something was actually recorded are addressable, so the playhead can never sit on a date
  // the desk knew nothing new.
  const [scrubIndex, setScrubIndex] = useState<number | null>(null);
  const activeIndex = scrubIndex ?? Math.max(0, plotted.length - 1);
  const active = plotted[activeIndex];

  const xOf = (i: number): number => {
    const usable = W - PAD.left - PAD.right;
    if (plotted.length <= 1) return PAD.left + usable / 2;
    return PAD.left + (i / (plotted.length - 1)) * usable;
  };

  // A step path: horizontal to the next x at the current level, then vertical to the new one.
  const stepPath = useMemo(() => {
    if (!plotted.length) return '';
    let path = `M ${xOf(0)} ${levelY(plotted[0].level)}`;
    for (let i = 1; i < plotted.length; i++) {
      path += ` L ${xOf(i)} ${levelY(plotted[i - 1].level)}`;
      path += ` L ${xOf(i)} ${levelY(plotted[i].level)}`;
    }
    return path;
  }, [plotted]);

  return (
    <WidgetContainer title={title ?? 'Documented appetite over time'} source="client knowledge graph">
      {query.isLoading ? (
        <div className="py-8 text-center">
          <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-[#002B45] border-t-transparent" />
        </div>
      ) : query.isError ? (
        <WidgetErrorState
          title="Couldn't load the appetite history"
          detail="The knowledge graph didn't respond."
          onRetry={() => void query.refetch()}
        />
      ) : !data?.sufficient ? (
        // Honest empty. NEVER a line through one dot, and never an interpolated one.
        <div data-testid="timeline-insufficient" className="space-y-1 py-4 text-sm text-muted-foreground">
          <p>No appetite history can be shown for this client.</p>
          {data?.reason && <p className="text-xs">{data.reason}.</p>}
          {!!data?.uncited_points && (
            <p className="text-xs">
              {data.uncited_points} recorded change(s) carry no source quote.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <svg
            data-testid="timeline-svg"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid meet"
            className="w-full"
            role="img"
            aria-label="Documented risk appetite over time against the mandate band"
          >
            {/* The mandate band — only when a profile was actually determined. */}
            {data.band && (
              <rect
                data-testid="timeline-band"
                x={PAD.left}
                y={levelY(data.band.max_level) - 10}
                width={W - PAD.left - PAD.right}
                height={levelY(data.band.min_level) - levelY(data.band.max_level) + 20}
                className="fill-[#002B45]/8"
              />
            )}

            {APPETITE_ROWS.map((row) => (
              <g key={row.level}>
                <line
                  x1={PAD.left}
                  x2={W - PAD.right}
                  y1={levelY(row.level)}
                  y2={levelY(row.level)}
                  className="stroke-border"
                  strokeWidth={1}
                  strokeDasharray="2 3"
                />
                <text
                  x={PAD.left - 8}
                  y={levelY(row.level) + 4}
                  textAnchor="end"
                  className="fill-muted-foreground text-[10px]"
                >
                  {row.label}
                </text>
              </g>
            ))}

            <path d={stepPath} className="stroke-[#002B45]" strokeWidth={2} fill="none" />

            {plotted.map((point, i) => (
              <g key={`${point.at}-${i}`}>
                <circle
                  data-testid="timeline-point"
                  cx={xOf(i)}
                  cy={levelY(point.level)}
                  r={i === activeIndex ? 5.5 : 3.5}
                  className={i === activeIndex ? 'fill-[#002B45]' : 'fill-background stroke-[#002B45]'}
                  strokeWidth={1.5}
                  onClick={() => setScrubIndex(i)}
                  style={{ cursor: 'pointer' }}
                />
                <text
                  x={xOf(i)}
                  y={H - 8}
                  textAnchor="middle"
                  className="fill-muted-foreground text-[9px]"
                >
                  {point.at.slice(5)}
                </text>
              </g>
            ))}
          </svg>

          {plotted.length > 1 && (
            <input
              type="range"
              aria-label="Scrub the appetite history"
              data-testid="timeline-scrubber"
              min={0}
              max={plotted.length - 1}
              step={1}
              value={activeIndex}
              onChange={(e) => setScrubIndex(Number(e.target.value))}
              className="w-full"
            />
          )}

          {active && (
            <div data-testid="timeline-active" className="rounded-md border border-border bg-muted/40 p-2.5">
              <p className="text-xs font-medium text-foreground">
                As of {active.at} — {active.appetite}
                {data.band && (
                  <span
                    data-testid="timeline-band-verdict"
                    className={
                      active.level >= data.band.min_level && active.level <= data.band.max_level
                        ? 'ml-2 text-green-700'
                        : 'ml-2 text-amber-700'
                    }
                  >
                    {active.level >= data.band.min_level && active.level <= data.band.max_level
                      ? `inside the ${data.band.profile} mandate band`
                      : `outside the ${data.band.profile} mandate band`}
                  </span>
                )}
              </p>
              {active.what && <p className="mt-1 text-[12px] text-muted-foreground">{active.what}</p>}
              {/* The provenance of the entry that moved it (TASK-682/683). */}
              {active.excerpt ? (
                <div className="mt-1.5 flex items-start gap-1.5">
                  <Quote className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  <p data-testid="timeline-excerpt" className="text-[12px] italic text-foreground">
                    “{active.excerpt}”
                    <span className="ml-1 not-italic text-[10px] text-muted-foreground">
                      said {active.observation_count}×
                      {locatorLabel(active.locator) && ` · ${locatorLabel(active.locator)}`}
                    </span>
                  </p>
                </div>
              ) : (
                <p data-testid="timeline-no-excerpt" className="mt-1.5 text-[11px] text-muted-foreground">
                  No source quote was recorded for this change.
                </p>
              )}
            </div>
          )}

          <p className="text-[11px] text-muted-foreground">
            {data.plotted_count} observation(s) across {data.distinct_dates} date(s). Values hold
            between observations — nothing is interpolated. Documented history, not a compliance
            attestation.
          </p>
        </div>
      )}
    </WidgetContainer>
  );
}

export default RiskTimeline;
